import React, { useCallback, useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { API } from '@Root/API'
import { HandbookTable } from '../HandbookTable'
import { useHandbookApi } from '../useHandbookApi'
import { Callout, ErrorState, FormBadge, LoadingState, Section, Steps, TileLink, TypeBadge } from '../ui'
import styles from '../style.module.scss'

const fetchCognito = () => API.handbook.getCognito()

export const CognitoOverview = () => {
  const { data, loading, error } = useHandbookApi(fetchCognito)

  if (loading) {
    return <LoadingState />
  }
  if (error) {
    return <ErrorState>Unable to load Cognito mapping data.</ErrorState>
  }

  return (
    <>
      <div className={styles.pageHead}>
        <p className={styles.kicker}>Cognito</p>
        <p className={styles.lead}>Specification for mapping Cognito Forms webhook data to TEID database fields.</p>
      </div>

      <Section title='Data flow'>
        <Steps
          items={[
            <>Cognito webhook sends nested JSON (<code>raw_data</code>).</>,
            <>Admin configures field mappings (Cognito path → TEID table.field).</>,
            <>
              <code>FormMappingService::transformFormData()</code> applies transformation rules.
            </>,
            <>
              Application forms → <code>ImportApplicationAction</code> writes to <code>applications</code> + <code>student_application</code> (mysql2).
            </>,
            <>
              Contact update forms → <code>ImportContactUpdateAction</code> creates a pending contact update.
            </>,
          ]}
        />
      </Section>

      <Section title='Form types'>
        <div className={styles.split}>
          <div className={styles.panel}>
            <p className={styles.panelTitle}>
              <FormBadge type='Application' />
            </p>
            <p className={styles.panelText}>Only student_applications table fields.</p>
          </div>
          <div className={styles.panel}>
            <p className={styles.panelTitle}>
              <FormBadge type='Contact update' />
            </p>
            <p className={styles.panelText}>All other tables (contacts, persons, student_details, and related).</p>
          </div>
        </div>
      </Section>

      <Section title='Cognito input formats'>
        <div className={styles.defList}>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>Simple text / email / date</span>
            <span className={styles.defDetail}>
              <code>john@example.com</code>, <code>1972-07-13</code>
            </span>
          </div>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>Lookup (id or label)</span>
            <span className={styles.defDetail}>
              Numeric id <code>206</code> on the <code>*_id</code> field with integer, or a clear label <code>Ukraine</code> on the <code>*_title</code> field
            </span>
          </div>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>Boolean</span>
            <span className={styles.defDetail}>true / false - use boolean transform</span>
          </div>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>Multi-select array</span>
            <span className={styles.defDetail}>
              <code>["Self/family", "Student Finance"]</code> or numeric ids <code>[1, 2]</code>
            </span>
          </div>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>Repeating sections</span>
            <span className={styles.defDetail}>Array of objects (HasMany fields)</span>
          </div>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>Address object</span>
            <span className={styles.defDetail}>
              Dot paths, e.g. <code>personal_information.address.City</code>
            </span>
          </div>
        </div>
      </Section>

      <Section title='Auto-resolution' hint='No transform rule is required for these mappings.'>
        <div className={styles.defList}>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>
              <code>*_title</code>
            </span>
            <span className={styles.defDetail}>
              Resolved to <code>*_id</code> via database label lookup
            </span>
          </div>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>
              <code>sending_diocese_title</code>
            </span>
            <span className={styles.defDetail}>Contact id via organisation name</span>
          </div>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>
              <code>british_visa_status</code>
            </span>
            <span className={styles.defDetail}>Enum match by label</span>
          </div>
          <div className={styles.defRow}>
            <span className={styles.defTerm}>
              <code>progress_stage</code>
            </span>
            <span className={styles.defDetail}>1-based index from the option label or the numeric code</span>
          </div>
        </div>
      </Section>

      <Callout>
        On admin confirm/accept, a subset of fields is copied into the main TEID contact/student records.{' '}
        <Link to='/home/handbook/cognito/sync'>Open Application → main DB sync</Link>
      </Callout>

      <Section title='Transformation rules'>
        <HandbookTable
          columns={[
            { key: 'key', label: 'Rule', render: value => <code>{value}</code> },
            { key: 'name', label: 'Name' },
            { key: 'description', label: 'Description' },
          ]}
          rows={data?.transformation_rules || []}
        />
      </Section>

      <Section title='Mapped tables'>
        <div className={styles.tileGrid}>
          {(data?.tables || []).map(table => (
            <TileLink
              key={table.slug}
              to={`/home/handbook/cognito/fields/${table.slug}`}
              title={table.table}
              badge={<FormBadge type={table.form_type} />}
              meta={`${table.field_count} fields`}
            />
          ))}
        </div>
      </Section>
    </>
  )
}

export const CognitoSync = () => {
  const { data, loading, error } = useHandbookApi(API.handbook.getCognitoSync)

  if (loading) {
    return <LoadingState />
  }
  if (error) {
    return <ErrorState>Unable to load application sync map.</ErrorState>
  }

  return (
    <>
      <div className={styles.crumb}>
        <Link to='/home/handbook/cognito'>Cognito</Link>
        <span className={styles.crumbSep}>/</span>
        <span>Application sync</span>
      </div>
      <div className={styles.pageHead}>
        <p className={styles.kicker}>Confirm / accept</p>
        <p className={styles.lead}>
          Field mapping applied when an application is confirmed in Admissions. A subset of Application Form fields is written into main TEID contact and
          student records; remaining fields stay in the Application Form database.
        </p>
      </div>
      <Section title='Overview'>
        <Callout>{data?.overview}</Callout>
      </Section>
      <Section title='Fields copied into the main TEID database'>
        <HandbookTable
          columns={[
            { key: 'from', label: 'Application Form field', render: value => <code>{value}</code> },
            { key: 'to', label: 'Main TEID destination', render: value => <code>{value}</code> },
            { key: 'notes', label: 'Notes' },
          ]}
          rows={data?.copied || []}
        />
      </Section>
      <Callout>{data?.new_contact_only_note}</Callout>
      <Section title='Fields retained in the Application Form database'>
        <HandbookTable
          columns={[
            { key: 'group', label: 'Field group' },
            { key: 'fields', label: 'Fields', render: value => <code>{value}</code> },
          ]}
          rows={data?.retained || []}
        />
      </Section>
    </>
  )
}

export const CognitoFields = ({ table }) => {
  const location = useLocation()
  const [highlightId, setHighlightId] = useState('')
  const fetchFields = useCallback(() => API.handbook.getCognitoFields(table), [table])
  const { data, loading, error } = useHandbookApi(fetchFields)

  useEffect(() => {
    if (!data || !location.hash) {
      setHighlightId('')
      return undefined
    }

    const id = decodeURIComponent(location.hash.slice(1))
    const node = document.getElementById(id)
    if (!node) {
      setHighlightId('')
      return undefined
    }

    node.scrollIntoView({ block: 'center' })
    setHighlightId(id)
    return undefined
  }, [data, location.hash])

  if (loading) {
    return <LoadingState />
  }
  if (error) {
    return <ErrorState>Unable to load field catalog.</ErrorState>
  }

  const refLink = ref => {
    if (!ref) {
      return ''
    }
    const [slug, hash] = ref.split('#')
    return <Link to={`/home/handbook/reference/${slug}${hash ? `#${hash}` : ''}`}>Codes</Link>
  }

  return (
    <>
      <div className={styles.crumb}>
        <Link to='/home/handbook/cognito'>Cognito</Link>
        <span className={styles.crumbSep}>/</span>
        <span>{data?.table}</span>
      </div>
      <div className={styles.pageHead}>
        <p className={styles.kicker}>TEID table</p>
        <p className={styles.lead}>
          <code>{data?.table}</code> <FormBadge type={data?.form_type} />
        </p>
      </div>
      {data?.note && (
        <Callout>
          {data.note} See <Link to='/home/handbook/import/student-csv'>Student CSV import</Link>.
        </Callout>
      )}
      <Section title='Fields'>
        <HandbookTable
          filter
          rowId={row => `field-${row.key}`}
          highlightId={highlightId}
          columns={[
            { key: 'key', label: 'Field', render: value => <code>{value}</code> },
            { key: 'label', label: 'Label' },
            { key: 'type', label: 'Type', render: value => <TypeBadge type={value} /> },
            { key: 'storage', label: 'Storage' },
            { key: 'cognito', label: 'Cognito', render: value => <code>{value}</code> },
            { key: 'transform', label: 'Transform' },
            { key: 'ref', label: 'Reference', render: value => refLink(value) },
          ]}
          rows={data?.fields || []}
        />
      </Section>
      {data?.recommended_mappings?.length > 0 && (
        <Section title='Recommended mappings (Common Awards)'>
          <HandbookTable
            columns={[
              { key: 'cognito', label: 'Cognito field', render: value => <code>{value}</code> },
              { key: 'teid', label: 'TEID field', render: value => <code>{value}</code> },
              { key: 'transform', label: 'Transform' },
              { key: 'notes', label: 'Notes' },
            ]}
            rows={data.recommended_mappings}
          />
          <Callout>
            Full option code lists: <Link to='/home/handbook/reference/application-option-codes'>Application Option Codes</Link>. On confirm/accept, only a
            subset of these fields is copied into the main TEID database. <Link to='/home/handbook/cognito/sync'>Application → main DB sync</Link>
          </Callout>
        </Section>
      )}
    </>
  )
}
